/**
 * Allergy API - manage patient allergies.
 */
import { apiGet, apiGetList, apiPost, apiPut, apiDelete } from "./client";
import type {
  Allergy,
  CreateAllergyRequest,
  UpdateAllergyRequest,
} from "@/types";

/** List allergies for a patient (paginated). */
export async function listAllergies(
  patientId: string,
  params?: { page?: number; per_page?: number; status?: string },
): Promise<{ allergies: Allergy[]; page: number; totalPages: number }> {
  const page = params?.page ?? 1;
  const per_page = params?.per_page ?? 25;
  const status = params?.status;

  const resp = await apiGetList<Allergy>(`/patients/${patientId}/allergies`, {
    params: { page, per_page, ...(status ? { status } : {}) },
  });

  return {
    allergies: resp.data,
    page: resp.meta.page,
    totalPages: resp.meta.total_pages,
  };
}

/** List only active allergies for the allergy band (chart header). */
export async function listActiveAllergies(patientId: string): Promise<Allergy[]> {
  const resp = await apiGet<{ allergies: Allergy[] }>(
    `/patients/${patientId}/allergies/active`,
  );
  return resp.allergies ?? [];
}

/** Create a new allergy record for a patient. */
export function createAllergy(
  patientId: string,
  data: CreateAllergyRequest,
): Promise<Allergy> {
  return apiPost<Allergy>(`/allergies`, { patient_id: patientId, ...data });
}

/** Update an existing allergy record. */
export function updateAllergy(
  id: string,
  data: UpdateAllergyRequest,
): Promise<Allergy> {
  return apiPut<Allergy>(`/allergies/${id}`, data);
}

/** Delete an allergy record. */
export function deleteAllergy(id: string): Promise<void> {
  return apiDelete<void>(`/allergies/${id}`);
}

export interface PatientAllergyStatus {
  patient_id: string;
  organization_id?: string;
  nkda: boolean;
  updated_by?: string;
  updated_at?: string;
}

/** Get patient-level NKDA flag. */
export function getPatientAllergyStatus(
  patientId: string,
): Promise<PatientAllergyStatus> {
  return apiGet<PatientAllergyStatus>(`/patients/${patientId}/allergies/status`);
}

/** Set patient-level NKDA flag. */
export function setPatientAllergyStatus(
  patientId: string,
  nkda: boolean,
): Promise<PatientAllergyStatus> {
  return apiPut<PatientAllergyStatus>(`/patients/${patientId}/allergies/status`, {
    nkda,
  });
}

export interface AllergenCatalogEntry {
  display: string;
  allergen_type: string;
  allergen_code?: string;
  allergen_system?: string;
}

/** Search seeded allergen reference catalog. */
export async function searchAllergenCatalog(params?: {
  type?: string;
  q?: string;
  limit?: number;
}): Promise<AllergenCatalogEntry[]> {
  const rows = await apiGet<AllergenCatalogEntry[] | null>("/allergies/catalog", { params });
  return rows ?? [];
}
